import { useLanguage } from './hooks';

/**
 * Detects text direction based on content
 * Returns 'rtl' if the text contains primarily RTL characters (Hebrew, Arabic, etc.)
 * Returns 'ltr' if the text contains primarily LTR characters
 * Returns defaultDirection if the text is empty
 */
export function detectTextDirection(text: string, defaultDirection: 'rtl' | 'ltr' = 'ltr'): 'rtl' | 'ltr' {
  if (!text?.trim()) return defaultDirection;

  const rtlPattern =
    /[\u0590-\u05FF\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF]/;

  const firstStrong = text.match(
    /[\u0590-\u05FF\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFFA-Za-z]/
  );

  if (!firstStrong) return defaultDirection;

  return rtlPattern.test(firstStrong[0]) ? 'rtl' : 'ltr';
}

/**
 * Hook to detect text direction from a value
 * Returns the detected direction and a function to get direction class
 * Uses the current language's direction as the default for empty text
 */
export function useTextDirection(value: string): {
  direction: 'rtl' | 'ltr';
  directionClass: 'rtl' | 'ltr';
  textAlignClass: 'text-right' | 'text-left';
} {
  const { isRTL } = useLanguage();
  const defaultDirection = isRTL ? 'rtl' : 'ltr';
  const direction = detectTextDirection(value, defaultDirection);
  
  return {
    direction,
    directionClass: direction,
    textAlignClass: direction === 'rtl' ? 'text-right' : 'text-left',
  };
}
